/* eslint no-promise-executor-return: 1 */

import qs from 'qs';
import request from 'superagent';
import assign from 'object-assign';
import when from 'when';
import { escapeHTML, unescapeHTML } from 'underscore.string';
import i18next from 'i18next';

import CustomerActionCreators from 'actions/CustomersActionCreators';
import AdminServerActionCreators from 'actions/AdminServerActionCreators';
import AdminAppDispatcher from 'dispatcher/AdminAppDispatcher';
import AdminConstants from 'constants/AdminConstants';
import ToastStore, { showToast } from 'stores/ToastStore';
import USER_TYPES from 'constants/UserTypes';
import Axios from 'axios';
import SessionStore from '../stores/SessionStore';
import client from './rest/AdminClient';
import {
  getBearerGrantTypeAuthHeaders,
  getRequestHeaders,
} from '../helpers/auth_helpers';

const Actions = AdminConstants.ActionTypes;

const setStripeKey = () => {
  // Set Stripe keys to allow billing actions
  Stripe.setPublishableKey(STRIPE_KEY);
};

const defaultqueryPaginate = {
  page: 1,
  perPage: 10,
};

const defaultQueryParams = {
  page: 1,
  per_page: 25,
  paginate: true,
  sort_by: 'optimized_name',
};

const defaultSearchQuery = {
  select:
    'optimized_name company_name name first_name last_name carrier _type picture require_tracking_id email authorized scac_code is_broker',
  populate: [{ path: 'carrier', select: 'name authorized' }],
};

const searchTypeMap = {
  shippers: 'Shipper',
  shipper: 'Shipper',

  carriers: 'Carrier',
  carrier: 'Carrier',

  drivers: 'Driver',
  driver: 'Driver',

  master_shippers: 'Shipper',
  master_shipper: 'Shipper',

  booking_shippers: 'Shipper',
  booking_shipper: 'Shipper',

  admins: 'Admin',
  admin: 'Admin',

  vendors: 'Vendor',
  vendor: 'Vendor',

  consignees: 'Consignee',
  consignee: 'Consignee',

  warehouses: 'Warehouse',
  warehouse: 'Warehouse',
};

const api1 = 'api/1/';
const api2 = 'admin/api/v2/';
const api3 = 'api/v2/';

const ajax = {
  get(url, ...parts) {
    let newUrl = url;

    if (parts.length > 0) {
      if (parts[0] && parts[0].constructor === Object) {
        newUrl += `?${qs.stringify(parts[0])}`;
      } else {
        parts.forEach((part) => {
          newUrl += `/${part}`;
        });
      }
    }

    return client({
      method: 'GET',
      path: newUrl,
    });
  },

  method(method, url, data) {
    return client({
      method: method.toUpperCase(),
      path: url,
      entity: JSON.stringify(data),
    });
  },
};

const billingTypes = {
  carrier: 'bank-accounts',
  shipper: 'funding-sources',
};

// Shorcut to display messages to the user
function toast(message, level = 'error') {
  ToastStore.getNotifications().push({
    title: message,
    body: '',
    level,
  });
  ToastStore.emitChange();
}

export async function searchCustomers({
  queryText,
  customerTypes = [],
  authorizedOnly = false,
}) {
  const params = {};
  if (customerTypes.length > 0) {
    params._type = customerTypes.map(
      (customerType) => searchTypeMap[customerType],
    );
  }

  if (authorizedOnly) {
    params.authorized = true;
  }

  const customerQuery = {
    test: false,
    select: 'optimized_name name _type company_name',
    lean: true,
  };

  const query = assign(
    {},
    defaultQueryParams,
    {
      ...customerQuery,
      order: 'asc',
    },
    params,
  );
  query.name = escapeHTML(queryText);

  const res = await Axios.get(
    `${API_HOST}/${api2}users?${qs.stringify(query)}`,
    {
      headers: getRequestHeaders(),
    },
  );
  return (res.data?.items || [])
    .filter((item) => !!item.name || !!item.company_name)
    .map((item) => ({
      ...item,
      name: unescapeHTML(item.name),
      company_name: unescapeHTML(item.company_name),
    }));
}

function createLocation(data, success) {
  const { _id, ...payload } = data || {};
  const method = _id ? 'patch' : 'post';
  const url = _id ? `${api3}locations/${_id}` : `${api3}locations`;
  return ajax
    .method(method, url, payload)
    .then(success)
    .catch((err) => {
      console.log(err.stack || err);
      toast(i18next.t('SOMETHING_WENT_WRONG'));
    });
}

const getRecentActivityByEmail = async (email) => {
  const query = qs.stringify({ email });
  const result = await client({
    method: 'GET',
    path: `/api/audits/users/activity?${query}`,
  });

  return result.entity;
};

export const getCustomerQuickPayAuditEvents = async (id, options = {}) => {
  const queryParams = { ...defaultqueryPaginate, ...options };
  const queryString = qs.stringify(queryParams);
  const result = await client({
    method: 'GET',
    path: `/admin/api/quickpay/audits/${id}?${queryString}`,
  });
  return result.entity;
};

export const getCustomerAuditEvents = async (id, options = {}) => {
  const queryParams = { ...defaultqueryPaginate, ...options };
  const queryString = qs.stringify(queryParams);
  const result = await client({
    method: 'GET',
    path: `/api/audits/users/${id}?${queryString}`,
  });

  return result;
};

export function getShipper(id) {
  return ajax.get(`${api1}shippers`, id);
}

export function updateShipperFscConfig(id, fscConfig) {
  const url = `${api1}shippers/${id}/fsc-config`;

  return ajax.method('put', url, fscConfig);
}

export function updateShipperInvoiceDocumentPreferences(
  id,
  invoiceDocumentPreferences,
) {
  const url = `${api1}shippers/${id}/invoice-document-preferences`;

  return ajax.method('patch', url, {
    invoice_document_preferences: invoiceDocumentPreferences,
  });
}

export function getCustomer(id) {
  return ajax.get(`${api1}users`, id);
}

export function uploadSensitiveDocuments(files, carrier_id, document_type) {
  return new Promise((resolve, reject) => {
    const req = request.post(
      `${API_HOST}/${api3}users/${carrier_id}/upload-sensitive-document/${document_type}`,
    );
    req.set('Authorization', getBearerGrantTypeAuthHeaders());
    files.forEach((file) => {
      req.attach('documentFiles', file);
    });
    req.end((err, res) => {
      if (err) {
        return reject(res);
      }
      if (res?.body?.success) {
        resolve(res.body.carrier);
      } else {
        reject(res);
      }
    });
  });
}
function saveLegalEntity(userId, legal, bank, callback) {
  const payload = {};

  if (legal) payload.legal_entity = legal;
  if (bank) payload.bank_account_id = bank;

  ajax
    .method('post', `/api/v2/users/${userId}/bank-accounts`, payload)
    .then((response) => {
      AdminServerActionCreators.updatedBankAccount(userId, response.entity);
      callback(null, response.entity);
    })
    .catch((err) => {
      console.log('failed to update bank account...');
      AdminServerActionCreators.updateBankAccountFailed(userId, err);
      console.log(err.stack || err);
      callback(err);
    });
}

export function createBankAccount(data) {
  return new Promise((resolve, reject) => {
    if (data.account) {
      setStripeKey();
      Stripe.bankAccount.createToken(
        {
          country: 'US',
          currency: 'USD',
          routing_number: data.routing,
          account_number: data.account,
        },
        (status, response) => {
          if (response.error && response.error.message) {
            toast(response.error.message);
            return reject(response.error);
          }
          if (response.error) {
            toast('Error retrieving account token');
            return reject(response.error);
          }

          saveLegalEntity(
            data.userId,
            data.legal_entity,
            response.id,
            (err, res) => {
              if (err) {
                return reject(err);
              }
              return resolve(res);
            },
          );
        },
      );
    } else {
      saveLegalEntity(data.userId, data.legal_entity);
    }
  });
}

export async function getCustomerV2(id) {
  const {
    entity: { _type },
  } = await getCustomer(id);

  const type = _type?.toLowerCase();
  if (_type !== 'Admin') {
    const res = await ajax.get(`${api1}${type}s`, id);
    return res.entity;
  }

  const res = await ajax.get('/admin/api/v2/users', id);
  return res.entity;
}

export async function updateShipper(id, updates) {
  try {
    const res = await ajax.method('patch', `${api1}shippers/${id}`, updates);
    showToast(`${i18next.t('SHIPPER_UPDATE_SUCCESS')}!`, '', 'success');
    return res?.entity?.shipper;
  } catch (res) {
    showToast(i18next.t('SHIPPER_UPDATE_FAILED'), res.entity.error, 'error');
    throw res;
  }
}

export async function updateCarrier(id, updates) {
  try {
    const res = await ajax.method('patch', `${api1}carriers/${id}`, updates);
    if (res?.entity?.success) {
      AdminServerActionCreators.updatedCarrier(id, res.entity.carrier);
      return res.entity.carrier;
    }
    AdminServerActionCreators.updateCarrierFailed(res.entity.error);
  } catch (err) {
    AdminServerActionCreators.updateCarrierFailed(err);
    throw err;
  }
}

export async function savePortProIntegration(data) {
  const { payload } = data;
  try {
    const res = await ajax.method(
      'patch',
      `${api1}carriers/${payload.carrierId}/portpro`,
      payload,
    );
    if (res.entity.success) {
      AdminServerActionCreators.savePortProIntegration(res.entity.data);
    } else {
      AdminServerActionCreators.savePortProIntegrationFailed(res.entity.error);
    }

    return res.entity.data.portpro?.enabled;
  } catch (err) {
    AdminServerActionCreators.savePortProIntegrationFailed(err.entity.error);
  }
}

export function getCreditCards(userId) {
  return ajax.get(`/api/v2/users/${userId}/funding-sources`);
}

export function fetchCreditCards(userId, cardId) {
  return ajax
    .method('post', `/api/v2/users/${userId}/funding-sources/`, {
      credit_card_id: cardId,
    })
    .then((res) => res.entity?.data);
}

export async function sendShipperRateCardEmail(
  userId,
  recipients,
  recentRateCard,
) {
  try {
    const payload = { recipients, recentRateCard };
    const url = `${API_HOST}/api/rates/users/${userId}/send-rate-card`;
    const result = await Axios.post(url, payload, {
      headers: getRequestHeaders(),
    });
    if (result.data?.success) {
      showToast(
        i18next.t('RATE_CARD_EMAIL_SENT'),
        i18next.t('RATE_CARD_EMAIL_SENT'),
        'success',
      );
    }
  } catch (e) {
    showToast(
      i18next.t('RATE_CARD_EMAIL_FAILED'),
      i18next.t('RATE_CARD_EMAIL_FAILED'),
      'error',
    );
  }
}

const Utils = {
  getCustomer(id, includeDrivers = true) {
    let billingType;
    return getCustomer(id)
      .then((res) => {
        const type =
          res.entity && res.entity._type
            ? res.entity._type.toLowerCase()
            : res.entity._type;
        if (type !== USER_TYPES.WAREHOUSE.toLowerCase()) {
          const promises =
            type === 'admin'
              ? [ajax.get(`/admin/api/v2/users`, id)]
              : [ajax.get(`${api1}${type}s`, id)];

          billingType = billingTypes[type];
          if (billingType) {
            promises.push(ajax.get(`/api/v2/users/${id}/${billingType}`));
          }

          if (includeDrivers && type === 'carrier') {
            promises.push(ajax.get(`${api1}carriers/${id}/drivers`));
          }

          return when.all(promises);
        }
        return [res];
      })
      .then((res) => {
        const customer = res[0].entity;
        const billing = billingType ? res[1] : false;
        const drivers = billingType ? res[2] : res[1];

        if (billing) {
          // Replace dashes by underscore 'bank_accounts'
          const key = billingTypes[customer._type.toLowerCase()].replace(
            /-/g,
            '_',
          );
          customer[key] = billing.entity.data;
        }
        if (drivers) {
          customer.drivers = drivers.entity.drivers;
        }

        AdminServerActionCreators.receiveCustomer(customer);

        // return the result
        return res;
      })
      .catch((err) => {
        console.log(err.stack || err);
      });
  },

  getShipper(id) {
    return getShipper(id)
      .then((res) => {
        AdminServerActionCreators.receiveShipper(res.entity);
        return res;
      })
      .catch((err) => {
        console.log(err.stack || err);
      });
  },

  getCarrierNonAdmin(id) {
    return ajax
      .get(`${api1}carriers`, id)
      .then((res) => {
        AdminServerActionCreators.receiveCarrier(res.entity);
        return res;
      })
      .catch((err) => {
        console.log(err.stack || err);
      });
  },

  // Fetch quotes by shipper id, returns list of quotes associated to shipper
  fetchQuotesByShipper(id, query = {}) {
    const queryString = qs.stringify(query);
    request
      .get(API_HOST + `/api/1/quotes/shipper/${id}?${queryString}`)
      .set('Authorization', getBearerGrantTypeAuthHeaders())
      .then((res) => {
        AdminServerActionCreators.receiveShipperQuotes(res.body);
      })
      .catch((err) => {
        console.log(err.stack || err);
      });
  },

  fetchQuote(id) {
    request
      .get(API_HOST + `/api/1/quotes/${id}`)
      .set('Authorization', getBearerGrantTypeAuthHeaders())
      .then((res) => AdminServerActionCreators.receiveQuote(res.body.quote))
      .catch((err) => AdminServerActionCreators.receiveQuoteFail(err.message));
  },

  fetchQuoteUpdateUpdate(id) {
    request
      .get(API_HOST + `/api/1/quotes/${id}`)
      .set('Authorization', getBearerGrantTypeAuthHeaders())
      .then((res) =>
        AdminServerActionCreators.receiveQuotesAfterUpdate(res.body),
      )
      .catch((err) => AdminServerActionCreators.receiveQuoteFail(err.message));
  },

  getCarrierRmisDocuments(dot_number, callback) {
    request
      .post(API_HOST + `/api/1/carriers/rmis/${dot_number}`)
      .set('Authorization', getBearerGrantTypeAuthHeaders())
      .then((response) => {
        return callback(response.body.data.NonAttachedCarrierStatusRequestAPI);
      })
      .catch((err) => {
        console.log(err.stack || err);
      });
  },

  getCarrier(id) {
    return ajax
      .get(`${api2}carriers`, id)
      .then((res) => {
        AdminServerActionCreators.receiveCarrier(res.entity);
        return res;
      })
      .catch((err) => {
        console.log(err.stack || err);
      });
  },
  async getRecentActivityByEmail(email) {
    return getRecentActivityByEmail(email).catch((e) => {
      console.log('Error getting audits', e);
      return [];
    });
  },
  getShippers(args) {
    const query = assign({}, defaultQueryParams, args || {});

    return ajax
      .get(`${api2}shippers`, query)
      .then((res) => {
        AdminServerActionCreators.receiveShippers(res.entity);
        return res;
      })
      .catch((err) => {
        console.log(err.stack || err);
      });
  },

  getShippersWithTariff(tariffId) {
    const queryParams = {
      lean: true,
      sort_by: 'optimized_name',
    };
    const query = qs.stringify(
      assign({}, queryParams, { tariffs: [tariffId.toString()] }),
    );
    return ajax
      .get(`${api2}shippers?${query}`)
      .then((res) => {
        AdminServerActionCreators.receiveShippers(res.entity);
        return res;
      })
      .catch((err) => {
        console.log(err.stack || err);
      });
  },

  getCarriers(args) {
    const query = assign({}, defaultQueryParams, args || {});

    return ajax
      .get(`${api2}carriers`, query)
      .then((res) => {
        AdminServerActionCreators.receiveCarriers(res.entity.data);
        return res;
      })
      .catch((err) => {
        console.log(err.stack || err);
      });
  },

  getLocations(args) {
    const locationsQueryParams = { _id: { $in: args } };
    const query = assign({}, locationsQueryParams || {});

    return ajax
      .get(`${api1}locations`, query)
      .then((res) => {
        AdminServerActionCreators.receiveLocations(res.entity);
        return res;
      })
      .catch((err) => {
        console.log(err.stack || err);
      });
  },

  removeFavoriteLocation(shipperId, locationId) {
    const path = `${api1}shippers/${shipperId}/locations/${locationId}`;
    client({ method: 'DELETE', path })
      .then(() => {
        AdminServerActionCreators.removeFavoriteLocationSuccess(
          shipperId,
          locationId,
        );
      })
      .catch((err) =>
        AdminServerActionCreators.removeFavoriteLocationFail(err),
      );
  },
  getCustomerAuditEvents(id, options) {
    getCustomerAuditEvents(id, options).then(
      (response) => {
        AdminServerActionCreators.fetchCustomerAuditsSuccess({
          audits: response.entity,
        });
      },
      (error) => {
        AdminServerActionCreators.fetchCustomerAuditsFail({ error });
      },
    );
  },
  updateShipper(shipper) {
    return ajax
      .method('patch', `${api1}shippers/${shipper._id}`, shipper)
      .then((res) => {
        console.log(res.entity.shipper);
        AdminServerActionCreators.updatedShipper(
          shipper._id,
          res.entity.shipper,
        );
        return res;
      })
      .catch((err) => {
        console.log(err.stack || err);
        AdminServerActionCreators.receiveUpdateShipperFail(err);
      });
  },

  updateVendor(vendor) {
    return ajax
      .method('patch', `${api1}vendors/${vendor._id}`, vendor)
      .then((res) => {
        console.log(res.entity.vendor);
        AdminServerActionCreators.updatedVendor(vendor._id, res.entity.vendor);
        return res;
      })
      .catch((err) => {
        console.log(err.stack || err);
        AdminServerActionCreators.receiveUpdateShipperFail(err);
      });
  },

  async generateRateCard(payload = {}) {
    try {
      const url = `${PRICING_SERVICE_HOST}/lanes/rate-card`;
      const result = await Axios.post(url, payload, {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': PRICING_SERVICE_API_KEY,
          Authorization: getBearerGrantTypeAuthHeaders(),
        },
      });
      if (result.data?.success) {
        showToast(
          i18next.t('SUCCESS_RATE_CARD_GENERATION'),
          i18next.t('SUCCESS_RATE_CARD_GENERATION'),
          'success',
        );
      }
      return result.data;
    } catch (e) {
      showToast(
        i18next.t('FAILED_RATE_CARD_GENERATION'),
        i18next.t('FAILED_RATE_CARD_GENERATION'),
        'error',
      );
    }
  },

  async updateWarehouseUser(warehouseuser) {
    try {
      const url = `${API_HOST}/api/v2/warehouse/${warehouseuser._id}`;
      const result = await Axios.patch(url, warehouseuser, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: getBearerGrantTypeAuthHeaders(),
        },
      });

      if (result?.data?.success) {
        CustomerActionCreators.getCustomer(warehouseuser._id);
        showToast(
          i18next.t('SUCCESS_UPDATE_WAREHOUSE_USER'),
          i18next.t('SUCCESS_UPDATE_WAREHOUSE_USER'),
          'success',
        );
      }
      return result.data;
    } catch (e) {
      showToast(
        i18next.t('FAILED_UPDATE_WAREHOUSE_USER'),
        i18next.t('FAILED_UPDATE_WAREHOUSE_USER'),
        'error',
      );
    }
  },
  updateAdmin(admin) {
    return ajax
      .method('put', `${api1}users/${admin._id}`, admin)
      .then((res) => {
        CustomerActionCreators.getCustomer(admin._id);
        CustomerActionCreators.updateAdminSuccess(res);
        return res;
      })
      .catch((err) => {
        console.log(err.stack || err);
        CustomerActionCreators.updateAdminFails(err);
      });
  },
  updateConsignee(consignee) {
    return ajax
      .method('patch', `${api1}consignees/${consignee._id}`, consignee)
      .then((res) => {
        if (res.entity.success) {
          showToast(i18next.t('UPDATED_CONSIGNEE_DETAILS'), '', 'success');
          CustomerActionCreators.getCustomer(consignee._id);
          return res;
        }
        showToast(
          i18next.t('FAILED_TO_UPDATED_CONSIGNEE_DETAILS'),
          '',
          'error',
        );
      })
      .catch((err) => {
        console.log(err.stack || err);
        showToast(i18next.t('SOMETHING_WENT_WRONG'), '', 'error');
      });
  },
  inviteConsignee(consignee) {
    return ajax
      .method('post', `${api2}password/create/send`, {
        userIds: [consignee._id],
      })
      .then((res) => {
        if (res?.entity?.results.length && res.entity.results[0].success) {
          showToast(i18next.t('SUCCESSFULLY_INVITED_CONSIGNEE'), '', 'success');
          CustomerActionCreators.getCustomer(consignee._id);
          return res;
        }
        showToast(i18next.t('CONSIGNEE_INVITE_FAILED'), '', 'error');
      })
      .catch((err) => {
        console.log(err.stack || err);
        showToast(i18next.t('SOMETHING_WENT_WRONG'), 'error');
      });
  },
  updateShipperInvoiceDocumentPreferences(id, invoiceDocumentPreferences) {
    return updateShipperInvoiceDocumentPreferences(
      id,
      invoiceDocumentPreferences,
    )
      .then((res) => {
        AdminServerActionCreators.updatedShipper(id, res.entity.shipper);
        return res;
      })
      .catch((err) => {
        console.log(err.stack || err);
        AdminServerActionCreators.receiveUpdateShipperFail(err);
      });
  },

  updateShipperComments(id, message) {
    const comment = { message };
    return ajax
      .method('post', `${api1}shippers/${id}/comment`, comment)
      .then((res) => {
        AdminServerActionCreators.updatedShipper(id, res.entity.shipper);
        return res;
      })
      .catch((err) => {
        console.log(err.stack || err);
        AdminServerActionCreators.receiveUpdateShipperFail(err);
      });
  },

  assignTariff(shipper, tariff, password) {
    const entity = { tariff_id: tariff._id, password };
    return ajax
      .method('post', `${api2}shippers/${shipper._id}/tariffs`, entity)
      .then((res) => {
        AdminServerActionCreators.receiveAssignTariffSuccess(res.entity);
        return res;
      })
      .catch((err) => {
        console.log(err.stack || err);
        AdminServerActionCreators.receiveAssignTariffFail(err);
      });
  },

  unassignTariff(shipper, tariff, password) {
    const path = `${api2}shippers/${shipper._id}/tariffs`;
    const entity = JSON.stringify({ tariff_id: tariff._id, password });
    client({ method: 'DELETE', path, entity })
      .then((res) => {
        AdminServerActionCreators.receiveUnassignTariffSuccess(res.entity);
        return res;
      })
      .catch((err) => {
        console.log(err.stack || err);
        AdminServerActionCreators.receiveUnassignTariffFail(err);
      });
  },

  updateCarrierDocuments(carrier) {
    return ajax
      .method('patch', `${api1}carriers/${carrier._id}`, carrier)
      .then((res) => {
        AdminServerActionCreators.updateCarrierDocumentsSuccess(
          carrier._id,
          res.entity.carrier,
        );
        return res;
      })
      .catch((err) => {
        console.log(err.stack || err);
        AdminServerActionCreators.updateCarrierDocumentsFail(err);
      });
  },
  updateCarrier(carrier) {
    return ajax
      .method('patch', `${api1}carriers/${carrier._id}`, carrier)
      .then((res) => {
        if (res.entity.success) {
          AdminServerActionCreators.updatedCarrier(
            carrier._id,
            res.entity.carrier,
          );
          return res;
        }
        AdminServerActionCreators.updateCarrierFailed(res.entity.error);
      })
      .catch((err) => {
        console.log(err.stack || err);
        AdminServerActionCreators.updateCarrierFailed(err);
      });
  },

  updateDriver(driver) {
    return ajax
      .method('patch', `${api1}drivers/${driver._id}`, driver)
      .then((res) => {
        if (res.entity.success) {
          AdminServerActionCreators.updatedDriver(
            driver._id,
            res.entity.driver,
          );
        } else {
          AdminServerActionCreators.updateDriverFailed(res.entity.error);
        }
      })
      .catch((err) => {
        console.log(err.stack || err);
        AdminServerActionCreators.updateDriverFailed(err.entity.error);
      });
  },

  createOrigin(data) {
    return createLocation(data, (res) => {
      AdminServerActionCreators.createdOrigin(res.entity);
      return res;
    });
  },

  createDestination(data) {
    return createLocation(data, (res) => {
      AdminServerActionCreators.createdDestination(res.entity);
      return res;
    });
  },

  updatePassword(id, password) {
    return ajax
      .method('put', 'users/password', {
        user_id: id,
        password,
      })
      .then((res) => {
        AdminServerActionCreators.updatedPassword();
        return res;
      })
      .catch((err) => {
        console.log(err.stack || err);
        AdminServerActionCreators.updatedPasswordFail(err?.entity);
      });
  },

  getDriver(id) {
    return ajax
      .get(`${api1}drivers/${id}`)
      .then((res) => {
        AdminServerActionCreators.receiveDriver(res.entity);
        return res;
      })
      .catch((err) => {
        console.log(err.stack || err);
      });
  },

  getDrivers(args) {
    const query = assign({}, defaultQueryParams, args || {});

    return ajax
      .get(`${api2}drivers`, query)
      .then((res) => {
        AdminServerActionCreators.receiveDrivers(res.entity.data);
        return res;
      })
      .catch((err) => {
        console.log(err.stack || err);
      });
  },
  customerSearchOptions: {
    general: {
      test: false,
      select: 'optimized_name name company_name _type picture email is_broker',
      lean: false,
    },
    drivers: {
      test: false,
      select:
        'optimized_name first_name last_name carrier _type picture email authorized is_broker',
      populate: [{ path: 'carrier', select: 'name status is_broker' }],
      lean: true,
    },
    carriers: {
      test: false,
      select:
        'optimized_name name _type default_driver picture email authorized scac_code status portpro is_broker',
      lean: true,
    },
    vendors: {
      test: false,
      select: 'optimized_name company_name _type picture email is_broker',
      lean: true,
    },
    consignees: {
      test: false,
      select: 'optimized_name company_name _type picture email is_broker',
      lean: true,
    },
    shippers: {
      test: false,
      select:
        'optimized_name company_name _type picture require_tracking_id email is_broker',
      lean: false,
    },
    booking_shippers: {
      test: false,
      select:
        'optimized_name company_name email enterprise markup tariffs origin destination locations _type picture accounting require_tracking_id is_broker',
      populate: [{ path: 'origin' }, { path: 'destination' }],
      lean: false,
    },
    master_shippers: {
      test: false,
      select: 'optimized_name _type company_name picture email is_broker',
      lean: true,
    },
    admins: {
      test: false,
      select: 'optimized_name first_name last_name _type email is_broker',
      lean: true,
    },
  },
  getCustomersByList(customers = []) {
    const customerQuery = this.customerSearchOptions.general;
    const query = assign({}, customerQuery, {
      customers,
    });

    ajax.get(`${api2}users?${qs.stringify(query)}`).then(
      (res) => {
        AdminServerActionCreators.searchCustomersSuccess(res.entity.items);
      },
      (err) => {
        AdminServerActionCreators.searchCustomersFail(
          err.entity ? err.entity.err || {} : err || {},
        );
      },
    );
  },
  searchCustomers({
    customerType,
    name,
    selectFields = [],
    authorizedOnly = false,
    userTag = '',
  }) {
    const params = {};
    if (customerType) {
      if (customerType === 'master_shippers') {
        params.isMasterAccount = true;
      }
      params._type = searchTypeMap[customerType];
    }
    if (name) {
      params.name = name;
    }

    if (authorizedOnly) {
      params.authorized = true;
    }

    if (userTag) {
      params.tags = userTag;
    }

    const customerQuery = customerType
      ? this.customerSearchOptions[customerType]
      : defaultSearchQuery;
    if (selectFields.length > 0) {
      customerQuery.select = customerQuery.select
        .split(' ')
        .concat(selectFields)
        .join(' ');
    }
    const query = assign(
      {},
      defaultQueryParams,
      {
        ...customerQuery,
        order: 'asc',
      },
      params,
    );
    if (query.name) {
      query.name = escapeHTML(query.name);
    }

    if (SessionStore.isCarrier()) {
      ajax
        .get(`${api3}users/associated?context=shipments&${qs.stringify(query)}`)
        .then(
          (res) => {
            AdminServerActionCreators.searchCustomersSuccess(res.entity.items);
          },
          (err) => {
            AdminServerActionCreators.searchCustomersFail(
              err.entity ? err.entity.err || {} : err || {},
            );
          },
        );
    } else {
      ajax.get(`${api2}users?${qs.stringify(query)}`).then(
        (res) => {
          AdminServerActionCreators.searchCustomersSuccess(res.entity.items);
        },
        (err) => {
          AdminServerActionCreators.searchCustomersFail(
            err.entity ? err.entity.err || {} : err || {},
          );
        },
      );
    }
  },

  searchQuotes(search) {
    const params = {};
    const user = SessionStore.getUser();

    if (search) {
      params.quote_id = { $regex: search.data, $options: 'i' };
    }

    if (SessionStore.isShipper()) {
      params.shipper = user._id;
    }

    if (SessionStore.isAdmin() && search.shipper) {
      params.shipper = search.shipper;
    }

    const query = assign({}, params);

    ajax.get(`/api/2/quotes/search?${qs.stringify(query)}`).then(
      (res) => {
        AdminServerActionCreators.searchQuotesSuccess(res.entity);
      },
      (err) => {
        AdminServerActionCreators.searchQuotesFail(err.entity.error || {});
      },
    );
  },

  createDriver(data) {
    return ajax
      .method('post', `${api1}drivers`, data)
      .then((results) => {
        AdminServerActionCreators.receiveCreatedDriver(data);
        return results;
      })
      .catch((err) => {
        console.log(err.stack || err);
      });
  },

  createDefaultDriver(id) {
    return ajax
      .method('post', `${api1}carriers/${id}/default-driver`, {})
      .then((res) => {
        const parsedResponse = res.entity;
        if (parsedResponse.success) {
          AdminServerActionCreators.updatedCarrier(id, parsedResponse.carrier);
          AdminServerActionCreators.receiveCreatedDriver(parsedResponse.driver);
        } else {
          AdminServerActionCreators.updateCarrierFailed(parsedResponse.error);
        }
        return res;
      })
      .catch((res) => {
        AdminServerActionCreators.updateCarrierFailed(res.entity.error);
      });
  },
  getCreditCards(userId) {
    return ajax
      .get(`/api/v2/users/${userId}/funding-sources`)
      .then((response) => {
        AdminServerActionCreators.receiveCreditCards(response.entity.data);
        return response;
      })
      .catch((err) => {
        console.log(err.stack || err);
      });
  },

  getBankAccounts(userId) {
    return ajax
      .get(`/api/v2/users/${userId}/bank-accounts`)
      .then((response) => {
        AdminServerActionCreators.receiveBankAccounts(response.entity.data);
        return response;
      })
      .catch((err) => {
        console.log(err.stack || err);
      });
  },

  createCreditCard(data) {
    setStripeKey();
    Stripe.card.createToken(
      {
        number: data.number,
        cvc: data.cvv,
        exp_month: data.expiry_month,
        exp_year: data.expiry_year,
      },
      (status, response) => {
        if (response.error) {
          toast(i18next.t('ERROR_GETTING_CARD_TOKEN'));
          return;
        }

        ajax
          .method('post', `/api/v2/users/${data.userId}/funding-sources/`, {
            credit_card_id: response.id,
          })
          .then((resp) => {
            AdminServerActionCreators.updatedCreditCard(
              data.userId,
              resp.entity.data,
            );
          })
          .catch((err) => {
            console.log(err.stack || err);
          });
      },
    );
  },

  /**
   * Saves a bank account and the legal entity for a shipper
   * all at once.
   * @param  {Object} data {account, legal_entity}
   */
  createBankAccount(data) {
    if (data.account) {
      setStripeKey();
      Stripe.bankAccount.createToken(
        {
          country: 'US',
          currency: 'USD',
          routing_number: data.routing,
          account_number: data.account,
        },
        (status, response) => {
          if (response.error && response.error.message) {
            toast(response.error.message);
            return;
          }
          if (response.error) {
            toast('Error retrieving account token');
            return;
          }

          this.saveLegalEntity(data.userId, data.legal_entity, response.id);
        },
      );
    } else {
      this.saveLegalEntity(data.userId, data.legal_entity);
    }
  },

  /**
   * Saves the legal entity for a carrier.
   * @param  {Object} legal Legal entity data.
   * @param  {String} bank  Stripe bank token.
   */
  saveLegalEntity(userId, legal, bank) {
    const payload = {};

    if (legal) payload.legal_entity = legal;
    if (bank) payload.bank_account_id = bank;

    ajax
      .method('post', `/api/v2/users/${userId}/bank-accounts`, payload)
      .then((response) => {
        AdminServerActionCreators.updatedBankAccount(userId, response.entity);
      })
      .catch((err) => {
        console.log('failed to update bank account...');
        AdminServerActionCreators.updateBankAccountFailed(userId, err);
        console.log(err.stack || err);
      });
  },

  createCharge(userId, data) {
    return ajax
      .method('post', `/api/v2/users/${userId}/charges/`, data)
      .then((response) => {
        AdminServerActionCreators.createdCharge(response.entity.data);
        return response;
      })
      .catch((res) => {
        AdminServerActionCreators.failedCharge(res);
      });
  },

  createPayout(userId, data) {
    return ajax
      .method('post', `/api/v2/users/${userId}/transfers/`, data)
      .then((response) => {
        AdminServerActionCreators.createdPayout(response.entity.data);
        return response;
      })
      .catch((res) => {
        AdminServerActionCreators.failedPayout(res);
      });
  },

  getStripeCharges() {
    console.log('getStripeCharges not implemented');
    const dummyData = [
      {
        id: 'ch_16BN2eGlvhzUM2JpSDmYW7Sb',
        amount: 11400,
        created: 1433786477,
        status: 'succeeded',
        failure_code: null,
        failure_message: null,
        description: 'Cargomatic one-time charge',
        statement_descriptor: 'Cargomatic Invoice',
        refunded: false,
        amount_refunded: 0,
        source: {
          last4: '4242',
          brand: 'Visa',
          exp_month: 12,
          exp_year: 2019,
        },
      },
      {
        id: 'ch_16BN2eGlvhzUM2JpSDmYW7Sc',
        amount: 4050,
        created: 1433756477,
        status: 'failed',
        failure_code: 'err',
        failure_message: 'Not enough funds.',
        description: 'Cargomatic one-time charge',
        statement_descriptor: 'Cargomatic Invoice',
        refunded: false,
        amount_refunded: 0,
        source: {
          last4: '4242',
          brand: 'Visa',
          exp_month: 12,
          exp_year: 2019,
        },
      },
      {
        id: 'ch_16BN2eGlvhzUM2JpSDmYW7Sd',
        amount: 114000,
        created: 1433686477,
        status: 'succeeded',
        failure_code: null,
        failure_message: null,
        description: 'Cargomatic one-time charge',
        statement_descriptor: 'Cargomatic Invoice',
        refunded: false,
        amount_refunded: 0,
        source: {
          last4: '4242',
          brand: 'Visa',
          exp_month: 12,
          exp_year: 2019,
        },
      },
    ];

    setTimeout(
      AdminServerActionCreators.receiveStripeCharges.bind(null, dummyData),
      100,
    );
    /* Call the endpoint
    return ajax.method('post', '/api/v2/users/' + userId + '/transfers/', data)
    .then(function(response) {
    AdminServerActionCreators.receiveStripeCharges(response.entity.data);
    return response;
  })
  .catch(function(err) {
  console.log(err.stack || err);
  })
  ;
  */
  },

  getStripePayouts() {
    console.log('getStripePayouts not implemented');
    const dummyData = [
      {
        id: 'tr_16BNL1GlvhzUM2JpNsWI2K1g',
        created: 1433785891,
        amount: 2021,
        status: 'paid',
        failure_code: null,
        failure_message: null,
        statement_descriptor: 'Cargomatic Payout',
        description: 'Cargomatic one-time payout',
        reversed: false,
        amount_reversed: 0,
      },
      {
        id: 'tr_16BNL1GlvhzUM2JpNsWI2K1a',
        created: 1433755891,
        amount: 17700,
        status: 'paid',
        failure_code: null,
        failure_message: null,
        statement_descriptor: 'Cargomatic Payout',
        description: 'Cargomatic one-time payout',
        reversed: false,
        amount_reversed: 0,
      },
      {
        id: 'tr_16BNL1GlvhzUM2JpNsWI2K1d',
        created: 1433705891,
        amount: 2312,
        status: 'paid',
        failure_code: null,
        failure_message: null,
        statement_descriptor: 'Cargomatic Payout',
        description: 'Cargomatic one-time payout',
        reversed: false,
        amount_reversed: 0,
      },
    ];
    setTimeout(
      AdminServerActionCreators.receiveStripePayouts.bind(null, dummyData),
      100,
    );
  },

  associateShipper(data) {
    return client({
      method: 'PUT',
      path: `admin/api/v2/shippers/${data.shipperId}/master_account`,
      entity: JSON.stringify({ master_account_id: data.masterId }),
    })
      .then((res) => {
        AdminServerActionCreators.updateCustomer(res.entity.data);
      })
      .catch((err) => {
        console.log(err);
      });
  },

  getActiveShipments() {
    return client({
      method: 'GET',
      path: 'api/v2/jobs/active',
    })
      .then((res) => {
        CustomerActionCreators.receiveActiveShipments(res.entity);
      })
      .catch((err) => {
        console.log(err);
      });
  },

  getShipperAccountRouting(scacCode) {
    return new Promise((resolve, reject) => {
      client({
        method: 'GET',
        path: `/admin/api/mappings?scacCode=${scacCode}`,
      })
        .then((res) => {
          if (res.entity && res.entity.doc) {
            return resolve(res.entity.doc);
          }
          resolve(null);
        })
        .catch((res) => {
          if (res.status.code === 404) {
            return resolve(null);
          }
          console.log('error res:', res);
          showToast(
            'Error getting account mapping',
            res.entity.error.message,
            'error',
          );
          reject(new Error(res.entity.error));
        });
    });
  },

  createShipperAccountRouting(mapping) {
    return new Promise((resolve, reject) => {
      client({
        method: 'POST',
        path: `/admin/api/mappings`,
        entity: JSON.stringify(mapping),
      }).then(
        (res) => {
          if (res.entity && res.entity.doc) {
            showToast('Created account mapping', '');
            return resolve(res.entity.doc);
          }
          resolve(null);
        },
        (res) => {
          console.log('error res:', res);
          showToast(
            'Error creating account mapping',
            res.entity.error.message,
            'error',
          );
          reject(new Error(res.entity.error));
        },
      );
    });
  },

  updateShipperAccountRouting(mapping) {
    return new Promise((resolve, reject) => {
      client({
        method: 'PUT',
        path: `/admin/api/mappings/${mapping._id}`,
        entity: JSON.stringify(mapping),
      }).then(
        (res) => {
          if (res.entity && res.entity.doc) {
            showToast(i18next.t('UPDATED_ACCOUNT_MAPPING'), '');
            return resolve(res.entity.doc);
          }
          resolve(null);
        },
        (res) => {
          console.log('error res:', res);
          showToast(
            i18next.t('ERROR_UPDATING_ACCOUNT_MAPPING'),
            res.entity.error.message,
            'error',
          );
          reject(new Error(res.entity.error));
        },
      );
    });
  },
};

// Fetch on action
AdminAppDispatcher.register((payload) => {
  const action = payload.action || {};
  const id = action.id;
  const args = action.args;
  const data = action.data || {};

  switch (payload.action.type) {
    case Actions.FETCH_QUOTES_BY_SHIPPER:
      Utils.fetchQuotesByShipper(id, action.options);
      break;
    case Actions.FETCH_QUOTE_REQUEST:
      Utils.fetchQuote(id);
      break;
    case Actions.CUSTOMER_FETCH:
      Utils.getCustomer(id, action.includeDrivers);
      break;
    case Actions.SHIPPER_FETCH:
      Utils.getShipper(id);
      break;
    case Actions.CARRIER_FETCH:
      Utils.getCarrier(id);
      break;
    case Actions.SHIPPERS_FETCH:
      Utils.getShippers(args);
      break;
    case Actions.CARRIERS_FETCH:
      Utils.getCarriers(args);
      break;
    case Actions.CUSTOMERS_FETCH:
      Utils.getCustomersByList(payload.action.customers);
      break;
    case Actions.SEARCH_CUSTOMERS_REQUEST:
      Utils.searchCustomers(payload.action);
      break;
    case Actions.SEARCH_QUOTES_REQUEST:
      Utils.searchQuotes(payload.action);
      break;
    case Actions.LOCATIONS_FETCH:
      Utils.getLocations(payload.action.locations);
      break;
    case Actions.SHIPPER_UPDATE:
      Utils.updateShipper(payload.action.shipper);
      break;
    case Actions.VENDOR_UPDATE:
      Utils.updateVendor(payload.action.vendor);
      break;
    case Actions.ADMIN_UPDATE:
      Utils.updateAdmin(payload.action.admin);
      break;
    case Actions.SHIPPER_UPDATE_COMMENTS:
      Utils.updateShipperComments(payload.action.id, payload.action.message);
      break;
    case Actions.CARRIER_UPDATE:
      Utils.updateCarrier(payload.action.carrier);
      break;
    case Actions.UPDATE_CARRIER_DOCUMENTS:
      Utils.updateCarrierDocuments(payload.action.carrier);
      break;
    case Actions.DRIVER_UPDATE:
      Utils.updateDriver(payload.action.driver);
      break;
    case Actions.ORIGIN_CREATE:
      Utils.createOrigin(payload.action.data);
      break;
    case Actions.DESTINATION_CREATE:
      Utils.createDestination(payload.action.data);
      break;
    case Actions.USER_PASSWORD_UPDATE:
      Utils.updatePassword(id, payload.action.password);
      break;
    case Actions.DRIVER_FETCH:
      Utils.getDriver(id);
      break;
    case Actions.DRIVERS_FETCH:
      Utils.getDrivers(args);
      break;
    case Actions.DRIVER_CREATE:
      Utils.createDriver(payload.action.data);
      break;
    case Actions.UPDATE_SHIPPER_INVOICE_DOCUMENT_PREFERENCES:
      Utils.updateShipperInvoiceDocumentPreferences(
        id,
        payload.action.invoiceDocumentPreferences,
      );
      break;
    case Actions.CREATE_DEFAULT_DRIVER:
      Utils.createDefaultDriver(payload.action.id);
      break;
    case Actions.CREATE_BANK_ACCOUNT:
      Utils.createBankAccount(payload.action.data);
      break;
    case Actions.SAVE_LEGAL_ENTITY:
      Utils.saveLegalEntity(
        data.userId,
        data.legal_entity,
        data.bank_account_id,
      );
      break;
    case Actions.CREATE_CREDIT_CARD:
      Utils.createCreditCard(payload.action.data);
      break;
    case Actions.BANK_ACCOUNTS_FETCH:
      Utils.getBankAccounts(id);
      break;
    case Actions.CREDIT_CARDS_FETCH:
      Utils.getCreditCards(id);
      break;
    case Actions.PAYOUT_CREATE:
      Utils.createPayout(id, {
        amount_in_cents: payload.action.amount,
        password: payload.action.password,
      });
      break;
    case Actions.CHARGE_CREATE:
      Utils.createCharge(id, {
        amount_in_cents: payload.action.amount,
        password: payload.action.password,
      });
      break;
    case Actions.STRIPE_PAYOUTS_FETCH:
      Utils.getStripePayouts(id);
      break;
    case Actions.STRIPE_CHARGES_FETCH:
      Utils.getStripeCharges(id);
      break;
    case Actions.ASSIGN_MASTER_ACCOUNT:
      Utils.associateShipper(args);
      break;
    case Actions.GET_ACTIVE_SHIPMENTS:
      Utils.getActiveShipments();
      break;
    case Actions.FETCH_QUOTE_AFTER_UPDATE:
      Utils.fetchQuoteUpdateUpdate(id);
      break;
    case Actions.REMOVE_FAVORITE_LOCATION_REQUEST:
      Utils.removeFavoriteLocation(
        payload.action.shipperId,
        payload.action.locationId,
      );
      break;
    default:
  }
});

export default Utils;
